/**
 * The shared chrome for everything that configures the workspace.
 *
 * People live at `/users`, not under `/settings/*` — but they are configuration
 * all the same, and jumping there from the settings rail used to drop you out
 * of it entirely. So the rail is a LAYOUT that wraps both route trees: the URL
 * stays `/users`, the surroundings stay put, and the rail highlights where you
 * actually are.
 */

import type { ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Bot,
  Brain,
  Building2,
  FileText,
  History,
  KeyRound,
  Plug,
  Receipt,
  Settings2,
} from 'lucide-react';
import { PageHead } from '@/components/layout/PageHead';
import { WhatsappIcon } from '@/components/ui/WhatsappIcon';
import { CalendarDays } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface SettingsLink {
  to: string;
  labelKey: string;
  descKey: string;
  /*
    Not `LucideIcon`: WhatsApp's mark is a brand glyph we draw ourselves, and
    Lucide will never ship it. Every icon in this rail is a component that takes
    a className and paints in currentColor, which is the only thing the rail
    actually needs from it.
  */
  icon: ComponentType<{ className?: string }>;
  /** Hidden unless the organization has this product switched on. */
  module?: string;
}

interface SettingsGroup {
  titleKey: string;
  links: SettingsLink[];
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    titleKey: 'set_group_company',
    links: [
      { to: '/settings/company', labelKey: 'settings_company', descKey: 'set_desc_company', icon: Building2 },
      { to: '/settings/tax', labelKey: 'settings_tax', descKey: 'set_desc_tax', icon: Receipt },
      { to: '/settings/branding', labelKey: 'settings_branding', descKey: 'set_desc_branding', icon: FileText },
      /* Leave settings live here rather than on the Leave screen: they are set
         up once and then left alone, and a rail of daily destinations is the
         wrong place for a page nobody opens twice. Module-gated with the rest
         of HR. */
      { to: '/settings/leave', labelKey: 'settings_leave', descKey: 'set_desc_leave', icon: CalendarDays, module: 'hr' },
    ],
  },
  {
    titleKey: 'set_group_workspace',
    links: [
      { to: '/settings/organization', labelKey: 'settings_organization', descKey: 'set_desc_org', icon: Settings2 },
      { to: '/users', labelKey: 'users', descKey: 'set_desc_users', icon: KeyRound },
      /*
        The activity log and the agent's memory moved in here off the main
        sidebar. Neither is something a person opens while doing their work:
        one answers "who changed this and when" and the other "why does it keep
        saying that", and both are questions you ask ABOUT the workspace rather
        than questions you ask of it. In the main rail they sat between Files
        and Billing, which is a list of daily destinations, and made that list
        longer for two pages nobody visits daily.

        Their URLs did not move. See the note in routes.tsx.
      */
      { to: '/activity', labelKey: 'activity_log', descKey: 'set_desc_activity', icon: History },
      { to: '/memory', labelKey: 'memory', descKey: 'set_desc_memory', icon: Brain, module: 'ai' },
    ],
  },
  // Billing is deliberately ABSENT from this rail. It lives at /billing with
  // its own entry in the sidebar — it is the subscription, not configuration,
  // and listing it in both places made the same destination look like two.
  {
    titleKey: 'set_group_integrations',
    links: [
      /* `Bot`, not `Sparkles`: the row is not "AI, generically", it is the one
         place you hand an outside assistant a door into this company. Sparkles
         is also the product's own AI glyph elsewhere, so it read as a second
         entrance to the built-in agent. */
      { to: '/settings/mcp', labelKey: 'settings_mcp', descKey: 'set_desc_mcp', icon: Bot, module: 'ai' },
      { to: '/settings/whatsapp', labelKey: 'settings_whatsapp', descKey: 'set_desc_whatsapp', icon: WhatsappIcon, module: 'ai' },
      /*
        NOT gated on a module, unlike its two neighbours. A workspace that
        switched Inventory or AI off still has three years of books in another
        system, and the way in from that system must not be the casualty of a
        toggle. Same reasoning as the `connectors` mount in routes/erp/index.ts.
      */
      { to: '/settings/connectors', labelKey: 'settings_connectors', descKey: 'set_desc_connectors', icon: Plug },
    ],
  },
];

function SettingsNav() {
  const { t } = useI18n();
  const { hasModule } = useAuth();

  return (
    <nav className="grid gap-6 lg:sticky lg:top-20" aria-label={t('settings')}>
      {SETTINGS_GROUPS.map((group) => {
        const links = group.links.filter((l) => !l.module || hasModule(l.module));
        if (links.length === 0) return null;
        return (
          <div key={group.titleKey} className="grid gap-0.5">
            <h6 className="eyebrow mb-1.5 px-3">{t(group.titleKey)}</h6>
            {/*
              Same active language as the product rail: a quiet neutral fill and
              a hairline of the accent on the start edge. Filling the whole row
              with brand-tinted colour made a settings list of six items look
              like six alerts.
            */}
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end
                className={({ isActive }) =>
                  cn(
                    'relative flex items-start gap-3 overflow-hidden rounded-lg px-3 py-2.5 transition-colors',
                    isActive
                      ? 'bg-accent'
                      : 'hover:bg-accent/50',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <link.icon
                      className={cn(
                        'mt-0.5 size-4 shrink-0',
                        isActive ? 'text-foreground/80' : 'text-muted-foreground/60',
                      )}
                    />
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'block text-[0.8125rem] leading-tight font-medium',
                          isActive ? 'text-foreground' : 'text-foreground/85',
                        )}
                      >
                        {t(link.labelKey)}
                      </span>
                      <span className="mt-0.5 block text-2xs leading-snug text-muted-foreground">
                        {t(link.descKey)}
                      </span>
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

export function SettingsLayout() {
  const { t } = useI18n();
  const { pathname } = useLocation();

  // The header describes the page you are on, not "settings" generically — a
  // subtitle about tax defaults while you are inviting a colleague is noise.
  const current = SETTINGS_GROUPS.flatMap((g) => g.links).find(
    (l) => pathname === l.to || pathname.startsWith(`${l.to}/`),
  );

  return (
    <>
      <PageHead
        eyebrow={current ? t('settings') : undefined}
        title={current ? t(current.labelKey) : t('settings')}
        subtitle={current ? t(current.descKey) : t('settings_subtitle')}
      />
      <div className="grid items-start gap-8 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <SettingsNav />
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </>
  );
}
